import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TokenIcon from '@mui/icons-material/Token';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loading } from '@/core/components';
import { useRace } from '@/core/contexts/RaceContext';
import { useAuth } from '@/core/hooks/useAuth';
import { useDocumentTitle } from '@/core/hooks/useDocumentTitle';
import { isAdmin } from '@/core/services/auth';

const menuItems = [
  { path: 'minting', label: 'ミント状況', icon: <TokenIcon /> },
  { path: 'results', label: '計測データ', icon: <AssessmentIcon /> },
  { path: 'fee', label: '手数料管理', icon: <AttachMoneyIcon /> },
];

export function AdminLayout() {
  const { user, loading: authLoading } = useAuth();
  const { race } = useRace();
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const adminGradient = theme.custom?.adminGradient || theme.custom?.gradient || theme.palette.primary.main;
  const [isAdminUser, setIsAdminUser] = useState<boolean | null>(null);
  const [checkingRole, setCheckingRole] = useState(true);

  // 現在の画面名を取得
  const currentPath = location.pathname.split('/').pop();
  const currentMenuItem = menuItems.find((item) => item.path === currentPath);
  const pageName = currentMenuItem?.label || '管理画面';

  // タイトル設定
  useDocumentTitle(`${pageName} - ${race.name} | RunPort NFT 管理画面`);

  const checkAdminRole = useCallback(async () => {
    if (!user) {
      setIsAdminUser(false);
      setCheckingRole(false);
      return;
    }

    const adminStatus = await isAdmin(user.uid);
    setIsAdminUser(adminStatus);
    setCheckingRole(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      checkAdminRole();
    }
  }, [authLoading, checkAdminRole]);

  if (authLoading || checkingRole) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Loading />
      </Container>
    );
  }

  if (!user) {
    return <Navigate to={`/${raceAlias}/admin/login`} replace />;
  }

  if (!isAdminUser) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">管理者権限がありません</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Box
        component="header"
        sx={{
          background: adminGradient,
          color: '#fff',
          py: 2,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          RunPort NFT {race.name}
          <Typography component="span" variant="h6" sx={{ fontWeight: 400, opacity: 0.9 }}>
            管理画面
          </Typography>
        </Typography>
      </Box>

      {/* メインコンテンツ */}
      <Container maxWidth="xl" sx={{ py: 4, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* サイドバー */}
          <Paper
            sx={{
              width: 200,
              flexShrink: 0,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={currentPath === item.path}
                  onClick={() => navigate(`/${raceAlias}/admin/${item.path}`)}
                  sx={{
                    '&.Mui-selected': {
                      background: adminGradient,
                      color: '#fff',
                      '& .MuiListItemIcon-root': {
                        color: '#fff',
                      },
                      '&:hover': {
                        background: adminGradient,
                        filter: 'brightness(0.9)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {/* コンテンツエリア */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
